import { useSyncExternalStore } from 'react';

export const OPERATING_GROUP_STORAGE_KEY = 'selectedOperatingGroupId';
export const OPERATING_GROUP_CHANGED_EVENT = 'crm:operating-group-changed';

export interface OperatingSelection {
  selectedGroupId: number | null;
  groups: Array<{ id: number; name: string }>;
  allGroupsOverview?: boolean;
  canViewAllGroups?: boolean;
  restaurantAuditReadsEnabled?: boolean;
}

export interface OperatingContextState {
  token: string | null;
  generation: number;
  status: 'initializing' | 'group' | 'all' | 'missing' | 'error';
  groupId: string | null;
  groups: OperatingSelection['groups'];
  canViewAllGroups: boolean;
  restaurantAuditReadsEnabled: boolean;
  error: string | null;
}

let state: OperatingContextState = {
  token: null, generation: 0, status: 'initializing', groupId: null,
  groups: [], canViewAllGroups: false, restaurantAuditReadsEnabled: false, error: null,
};
const listeners = new Set<() => void>();
export const getOperatingContext = () => state;
export const isOperatingContextReady = (context: OperatingContextState, token: string | null) =>
  !!token && context.token === token && token === localStorage.getItem('token')
    && (context.status === 'group' || context.status === 'all');

const publish = (next: OperatingContextState) => {
  state = next;
  listeners.forEach(listener => listener());
  window.dispatchEvent(new CustomEvent(OPERATING_GROUP_CHANGED_EVENT, { detail: { groupId: state.groupId } }));
};

export const beginOperatingContext = (token: string | null) => {
  publish({
    ...state, token, generation: state.generation + 1, status: 'initializing',
    groupId: null, groups: [], canViewAllGroups: false, restaurantAuditReadsEnabled: false, error: null,
  });
  return state.generation;
};

export const confirmOperatingContext = (generation: number, selection: OperatingSelection) => {
  if (state.generation !== generation || state.token !== localStorage.getItem('token')) return;
  const all = selection.allGroupsOverview === true && selection.canViewAllGroups === true;
  const group = selection.groups.find(item => item.id === selection.selectedGroupId);
  const groupId = all ? 'all' : group ? String(group.id) : null;
  if (groupId) localStorage.setItem(OPERATING_GROUP_STORAGE_KEY, groupId);
  else localStorage.removeItem(OPERATING_GROUP_STORAGE_KEY);
  publish({
    ...state, status: all ? 'all' : group ? 'group' : 'missing', groupId,
    groups: selection.groups, canViewAllGroups: selection.canViewAllGroups === true, error: null,
    restaurantAuditReadsEnabled: selection.restaurantAuditReadsEnabled === true,
  });
};

export const failOperatingContext = (generation: number, error: string) => {
  if (state.generation !== generation) return;
  publish({ ...state, status: 'error', groupId: null, error });
};

export const useOperatingContext = () => useSyncExternalStore(
  listener => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  getOperatingContext,
);
